import React, { useState } from 'react';
import { T, Var, useGT } from 'gt-react';
import {
  useSupplierPaymentDetails,
  useCreateSupplierPaymentDetails,
  useUpdateSupplierPaymentDetails,
  useDeleteSupplierPaymentDetails,
} from '@/api/rpc/hooks/accounts-payable/useSupplierPaymentDetails';
import type {
  SupplierPaymentDetails,
  CreateSupplierPaymentDetailsInput,
  UpdateSupplierPaymentDetailsInput,
} from '@erp/rpc';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Plus,
  PencilSimple,
  Trash,
  Bank,
  Star,
  Warning,
} from '@phosphor-icons/react';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const PAYMENT_METHODS = [
  { value: 'ach', label: 'ACH' },
  { value: 'wire', label: 'Wire' },
  { value: 'check', label: 'Check' },
  { value: 'fednow', label: 'FedNow' },
  { value: 'credit_card', label: 'Credit Card' },
  { value: 'card', label: 'Card' },
  { value: 'other', label: 'Other' },
] as const;

type PaymentMethodValue = (typeof PAYMENT_METHODS)[number]['value'];

function getMethodLabel(method: string): string {
  return PAYMENT_METHODS.find((m) => m.value === method)?.label ?? method;
}

// ---------------------------------------------------------------------------
// Form state
// ---------------------------------------------------------------------------

interface FormState {
  method: PaymentMethodValue;
  isDefault: boolean;
  label: string;
  bankName: string;
  accountNumber: string;
  confirmAccountNumber: string;
  routingNumber: string;
  confirmRoutingNumber: string;
}

const EMPTY_FORM: FormState = {
  method: 'ach',
  isDefault: false,
  label: '',
  bankName: '',
  accountNumber: '',
  confirmAccountNumber: '',
  routingNumber: '',
  confirmRoutingNumber: '',
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

interface SupplierPaymentDetailsSectionProps {
  supplierId: string;
  supplierEmail?: string | null;
}

export function SupplierPaymentDetailsSection({
  supplierId,
  supplierEmail,
}: SupplierPaymentDetailsSectionProps) {
  const gt = useGT();
  const hasEmail = Boolean(supplierEmail?.trim());
  const { paymentDetails, isLoading } = useSupplierPaymentDetails(supplierId);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingRecord, setEditingRecord] =
    useState<SupplierPaymentDetails | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);

  const createMutation = useCreateSupplierPaymentDetails({
    onSuccess: () => setDialogOpen(false),
  });
  const updateMutation = useUpdateSupplierPaymentDetails({
    onSuccess: () => setDialogOpen(false),
  });
  const deleteMutation = useDeleteSupplierPaymentDetails({
    onSuccess: () => setDeleteDialogOpen(false),
  });

  // ---- Handlers ----

  function handleOpenCreate() {
    setEditingRecord(null);
    setForm({
      ...EMPTY_FORM,
      isDefault: paymentDetails.length === 0,
    });
    setDialogOpen(true);
  }

  function handleOpenEdit(record: SupplierPaymentDetails) {
    setEditingRecord(record);
    setForm({
      method: record.method as PaymentMethodValue,
      isDefault: record.isDefault,
      label: record.label ?? '',
      bankName: record.bankName ?? '',
      // Don't prefill encrypted values — user must re-enter to change
      accountNumber: '',
      confirmAccountNumber: '',
      routingNumber: '',
      confirmRoutingNumber: '',
    });
    setDialogOpen(true);
  }

  function handleOpenDelete(id: string) {
    setDeletingId(id);
    setDeleteDialogOpen(true);
  }

  const [sameNumberDialogOpen, setSameNumberDialogOpen] = useState(false);

  // Live, per-field validation. Computed every render from the current form so
  // errors appear/clear as the user types. A confirmation mismatch is only
  // surfaced once the user has actually typed into the confirm field, to avoid
  // flashing "do not match" before they've had a chance to re-enter.
  const fieldErrors: Record<string, string> = {};
  if (form.accountNumber && !/^\d+$/.test(form.accountNumber)) {
    fieldErrors.accountNumber = gt('Account number must contain only digits', {
      context: 'validation error for account number',
    });
  }
  if (
    form.confirmAccountNumber &&
    form.confirmAccountNumber !== form.accountNumber
  ) {
    fieldErrors.confirmAccountNumber = gt('Account numbers do not match', {
      context: 'validation error when account number confirmation mismatches',
    });
  }
  if (form.routingNumber && !/^\d{9}$/.test(form.routingNumber)) {
    fieldErrors.routingNumber = gt('Routing number must be exactly 9 digits', {
      context: 'validation error for routing number',
    });
  }
  if (
    form.confirmRoutingNumber &&
    form.confirmRoutingNumber !== form.routingNumber
  ) {
    fieldErrors.confirmRoutingNumber = gt('Routing numbers do not match', {
      context: 'validation error when routing number confirmation mismatches',
    });
  }

  // Bank entry is only valid to submit when, for any number provided, it passes
  // its format check AND its confirmation matches exactly. In edit mode the bank
  // fields are not rendered (numbers are immutable), so these stay empty → valid.
  const bankEntryValid =
    (!form.accountNumber ||
      (/^\d+$/.test(form.accountNumber) &&
        form.confirmAccountNumber === form.accountNumber)) &&
    (!form.routingNumber ||
      (/^\d{9}$/.test(form.routingNumber) &&
        form.confirmRoutingNumber === form.routingNumber));

  // Account and routing numbers being identical is extremely unlikely (and
  // typically rejected by banks). Surface it as a soft warning, not a hard block.
  const accountRoutingSame =
    form.accountNumber.length > 0 && form.accountNumber === form.routingNumber;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    // Guard against submit paths that bypass the disabled button.
    if (!bankEntryValid) {
      return;
    }

    // On-save failsafe: confirm before persisting identical account/routing.
    if (accountRoutingSame) {
      setSameNumberDialogOpen(true);
      return;
    }

    submitForm();
  }

  function submitForm() {
    if (editingRecord) {
      // Bank account/routing numbers are immutable on edit — only metadata.
      const input: UpdateSupplierPaymentDetailsInput = {
        id: editingRecord.id,
        method: form.method,
        isDefault: form.isDefault,
        label: form.label || null,
        bankName: form.bankName || null,
      };
      updateMutation.mutate(input);
    } else {
      const input: CreateSupplierPaymentDetailsInput = {
        supplierId,
        method: form.method,
        isDefault: form.isDefault,
        label: form.label || undefined,
        bankName: form.bankName || undefined,
        accountNumber: form.accountNumber || undefined,
        routingNumber: form.routingNumber || undefined,
      };
      createMutation.mutate(input);
    }
  }

  function handleConfirmDelete() {
    if (!deletingId) return;
    deleteMutation.mutate({ id: deletingId });
  }

  const isSaving = createMutation.isPending || updateMutation.isPending;

  // ---- Render ----

  if (isLoading) {
    return (
      <div className="text-sm text-gray-500 py-4">
        Loading payment details...
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-semibold text-gray-700">
          <T context="section heading for payment details">Payment Details</T>
        </h4>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span tabIndex={hasEmail ? undefined : 0}>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleOpenCreate}
                  disabled={!hasEmail}
                >
                  <Plus className="h-3.5 w-3.5 mr-1" />
                  <T context="button label to add payment details">Add</T>
                </Button>
              </span>
            </TooltipTrigger>
            {!hasEmail && (
              <TooltipContent>
                <p>
                  <T>
                    A supplier email is required before adding bank accounts
                  </T>
                </p>
              </TooltipContent>
            )}
          </Tooltip>
        </TooltipProvider>
      </div>

      {paymentDetails.length === 0 ? (
        <div className="text-sm text-gray-400 py-3 text-center border rounded-lg">
          <T>No payment details configured</T>
        </div>
      ) : (
        <div className="space-y-2">
          {paymentDetails.map((record) => (
            <PaymentDetailCard
              key={record.id}
              record={record}
              onEdit={handleOpenEdit}
              onDelete={handleOpenDelete}
            />
          ))}
        </div>
      )}

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingRecord
                ? gt('Edit Payment Details', {
                    context: 'dialog title for editing payment details',
                  })
                : gt('Add Payment Details', {
                    context: 'dialog title for adding payment details',
                  })}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Method */}
            <div className="space-y-1.5">
              <Label htmlFor="method">
                <T context="label for payment method select">Payment Method</T>
              </Label>
              <Select
                value={form.method}
                onValueChange={(v) =>
                  setForm((f) => ({
                    ...f,
                    method: v as PaymentMethodValue,
                  }))
                }
              >
                <SelectTrigger id="method">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAYMENT_METHODS.map((m) => (
                    <SelectItem key={m.value} value={m.value}>
                      {m.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Label */}
            <div className="space-y-1.5">
              <Label htmlFor="label">
                <T context="label for payment details label input">
                  Label (optional)
                </T>
              </Label>
              <Input
                id="label"
                placeholder={gt('e.g. Primary Operating Account', {
                  context: 'payment details label input placeholder',
                })}
                value={form.label}
                onChange={(e) =>
                  setForm((f) => ({ ...f, label: e.target.value }))
                }
              />
            </div>

            {/* Bank Name */}
            <div className="space-y-1.5">
              <Label htmlFor="bankName">
                <T context="label for bank name input">Bank Name (optional)</T>
              </Label>
              <Input
                id="bankName"
                placeholder={gt('e.g. Chase', {
                  context: 'bank name input placeholder',
                })}
                value={form.bankName}
                onChange={(e) =>
                  setForm((f) => ({ ...f, bankName: e.target.value }))
                }
              />
            </div>

            {editingRecord ? (
              /* Bank numbers are immutable once created. Show them read-only. */
              <div className="space-y-1.5">
                <Label>
                  <T context="label for read-only bank numbers on edit">
                    Bank Numbers
                  </T>
                </Label>
                <div className="rounded-md border bg-gray-50 p-2.5 text-xs text-gray-600 space-y-1">
                  <div>
                    <T context="read-only masked account number">
                      Account:{' '}
                      <Var>{editingRecord.accountNumberMasked ?? '—'}</Var>
                    </T>
                  </div>
                  <div>
                    <T context="read-only masked routing number">
                      Routing:{' '}
                      <Var>{editingRecord.routingNumberMasked ?? '—'}</Var>
                    </T>
                  </div>
                  <p className="text-gray-400">
                    <T context="hint explaining bank numbers cannot be edited">
                      Account and routing numbers can&apos;t be changed. To use
                      different numbers, delete this method and add a new one.
                    </T>
                  </p>
                </div>
              </div>
            ) : (
              <>
                {/* Account Number */}
                <div className="space-y-1.5">
                  <Label htmlFor="accountNumber">
                    <T context="label for account number input">
                      Account Number
                    </T>
                  </Label>
                  <Input
                    id="accountNumber"
                    type="password"
                    autoComplete="off"
                    placeholder={gt('Enter account number', {
                      context: 'account number input placeholder',
                    })}
                    value={form.accountNumber}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, accountNumber: e.target.value }))
                    }
                  />
                  {fieldErrors.accountNumber && (
                    <p className="text-xs text-red-600">
                      {fieldErrors.accountNumber}
                    </p>
                  )}
                </div>

                {/* Confirm Account Number */}
                <div className="space-y-1.5">
                  <Label htmlFor="confirmAccountNumber">
                    <T context="label for confirm account number input">
                      Confirm Account Number
                    </T>
                  </Label>
                  <Input
                    id="confirmAccountNumber"
                    type="password"
                    autoComplete="off"
                    onPaste={(e) => e.preventDefault()}
                    placeholder={gt('Re-enter account number', {
                      context: 'confirm account number input placeholder',
                    })}
                    value={form.confirmAccountNumber}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        confirmAccountNumber: e.target.value,
                      }))
                    }
                  />
                  {fieldErrors.confirmAccountNumber && (
                    <p className="text-xs text-red-600">
                      {fieldErrors.confirmAccountNumber}
                    </p>
                  )}
                </div>

                {/* Routing Number */}
                <div className="space-y-1.5">
                  <Label htmlFor="routingNumber">
                    <T context="label for routing number input">
                      Routing Number
                    </T>
                  </Label>
                  <Input
                    id="routingNumber"
                    type="password"
                    autoComplete="off"
                    placeholder={gt('Enter routing number', {
                      context: 'routing number input placeholder',
                    })}
                    value={form.routingNumber}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, routingNumber: e.target.value }))
                    }
                  />
                  {fieldErrors.routingNumber && (
                    <p className="text-xs text-red-600">
                      {fieldErrors.routingNumber}
                    </p>
                  )}
                </div>

                {/* Confirm Routing Number */}
                <div className="space-y-1.5">
                  <Label htmlFor="confirmRoutingNumber">
                    <T context="label for confirm routing number input">
                      Confirm Routing Number
                    </T>
                  </Label>
                  <Input
                    id="confirmRoutingNumber"
                    type="password"
                    autoComplete="off"
                    onPaste={(e) => e.preventDefault()}
                    placeholder={gt('Re-enter routing number', {
                      context: 'confirm routing number input placeholder',
                    })}
                    value={form.confirmRoutingNumber}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        confirmRoutingNumber: e.target.value,
                      }))
                    }
                  />
                  {fieldErrors.confirmRoutingNumber && (
                    <p className="text-xs text-red-600">
                      {fieldErrors.confirmRoutingNumber}
                    </p>
                  )}
                </div>

                {/* Inline failsafe: account and routing numbers are identical */}
                {accountRoutingSame && (
                  <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-2.5 text-xs text-amber-800">
                    <Warning
                      className="h-4 w-4 shrink-0 mt-0.5"
                      weight="fill"
                    />
                    <span>
                      <T context="inline warning when account and routing numbers are identical">
                        The account number and routing number are identical.
                        This is extremely unusual — most banks issue different
                        values. Double-check both before saving.
                      </T>
                    </span>
                  </div>
                )}
              </>
            )}

            {/* Default toggle */}
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={form.isDefault}
                onChange={(e) =>
                  setForm((f) => ({ ...f, isDefault: e.target.checked }))
                }
                className="rounded border-gray-300"
              />
              <T context="checkbox label to set as default payment method">
                Set as default payment method
              </T>
            </label>

            <DialogFooter>
              <DialogClose asChild>
                <Button type="button" variant="ghost">
                  <T context="button label to cancel payment details form">
                    Cancel
                  </T>
                </Button>
              </DialogClose>
              <Button type="submit" disabled={isSaving || !bankEntryValid}>
                {isSaving
                  ? gt('Saving...', {
                      context: 'save payment details button label while saving',
                    })
                  : gt('Save', {
                      context: 'save payment details button label',
                    })}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Identical account/routing on-save confirmation */}
      <AlertDialog
        open={sameNumberDialogOpen}
        onOpenChange={setSameNumberDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              <T context="dialog title warning identical account and routing numbers">
                Account and routing numbers are identical
              </T>
            </AlertDialogTitle>
            <AlertDialogDescription>
              <T>
                The account number and routing number you entered are exactly
                the same. This almost never happens with real bank details and
                is usually a typo. Are you sure you want to save them as-is?
              </T>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              <T context="button label to go back and fix bank numbers">
                Go back
              </T>
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-amber-600 hover:bg-amber-700 focus:ring-amber-600"
              onClick={() => {
                setSameNumberDialogOpen(false);
                submitForm();
              }}
            >
              <T context="button label to save identical bank numbers anyway">
                Save anyway
              </T>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              <T context="dialog title for deleting payment details">
                Delete Payment Details
              </T>
            </AlertDialogTitle>
            <AlertDialogDescription>
              <T>
                Are you sure you want to delete this payment method? This action
                cannot be undone.
              </T>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              <T context="button label to cancel delete payment details">
                Cancel
              </T>
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={deleteMutation.isPending}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleteMutation.isPending
                ? gt('Deleting...', {
                    context:
                      'delete payment details button label while deleting',
                  })
                : gt('Delete', {
                    context: 'delete payment details button label',
                  })}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Card sub-component
// ---------------------------------------------------------------------------

interface PaymentDetailCardProps {
  record: SupplierPaymentDetails;
  onEdit: (record: SupplierPaymentDetails) => void;
  onDelete: (id: string) => void;
}

function PaymentDetailCard({
  record,
  onEdit,
  onDelete,
}: PaymentDetailCardProps) {
  return (
    <div className="flex items-start justify-between p-3 border rounded-lg">
      <div className="flex items-start gap-2.5 min-w-0">
        <Bank className="h-4 w-4 mt-0.5 text-gray-400 shrink-0" />
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium">
              {getMethodLabel(record.method)}
            </span>
            {record.isDefault && (
              <Badge variant="secondary" className="text-xs gap-0.5 py-0 h-5">
                <Star className="h-3 w-3" weight="fill" />
                <T context="badge label for default payment method">Default</T>
              </Badge>
            )}
            {record.label && (
              <span className="text-xs text-gray-500">{record.label}</span>
            )}
          </div>
          <div className="text-xs text-gray-500 mt-0.5 space-x-3">
            {record.bankName && <span>{record.bankName}</span>}
            {record.accountNumberMasked && (
              <span>
                <T context="account number display with masked value">
                  Acct: <Var>{record.accountNumberMasked}</Var>
                </T>
              </span>
            )}
            {record.routingNumberMasked && (
              <span>
                <T context="routing number display with masked value">
                  Routing: <Var>{record.routingNumberMasked}</Var>
                </T>
              </span>
            )}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-1 shrink-0 ml-2">
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          onClick={() => onEdit(record)}
        >
          <PencilSimple className="h-3.5 w-3.5" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-red-500 hover:text-red-700"
          onClick={() => onDelete(record.id)}
        >
          <Trash className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}
